/** @format */

import { Grid } from '@mui/material';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import { getExtranetEditorLanguageOptions, resolveExtranetApiLanguageCode, resolveExtranetSelectableLocale } from 'src/utils/extranetLanguages';
import { resolveSelectedLanguage } from 'src/utils/locale';
import { DOCUMENT_MAX_FILES } from 'src/constants';
import {
  addPart,
  closeSubmitErrorDialog,
  movePartDown,
  movePartToIndex,
  movePartUp,
  openSubmitErrorDialog,
  patchPart,
  removePart,
  resetContentEditor,
  setLocale,
  setMode,
  setContentId,
  setReleaseDate,
  setSelectedTags,
  setSendNotification,
  setTitle,
  toggleTopic,
  addTopicIdFromSearch,
  hydrateFromContentDetail,
  createContentPost,
  clearCreateContentPostError,
  updateContentPost,
  clearUpdateContentPostError,
  clearEditorDetailError,
  fetchContentPostForEditor
} from 'src/slices/contentEditor';
import { getPublishedTopics } from 'src/slices/topics';
import { isContentEditorPartFilled } from 'src/utils/contentEditor/isContentEditorPartFilled';
import {
  mapContentDetailToEditorPayload,
  toDuplicateEditorPayload,
  type ContentEditorPayload
} from 'src/utils/contentEditor/mapContentDetailToEditorPayload';
import { splitSelectedTags } from 'src/utils/contentEditor/contentEditorTags';
import { clearTagSearch, searchTags } from 'src/slices/tags';
import { useDispatch, useSelector } from 'src/store';
import ContentEditorBlocks from './contentEditor/ContentEditorBlocks';
import ContentEditorErrorDialog from './contentEditor/ContentEditorErrorDialog';
import ContentEditorPreview from './contentEditor/ContentEditorPreview';
import ContentEditorSettings from './contentEditor/ContentEditorSettings';
import { useFileManager } from 'src/hooks/useFileManager';

function cloneHydratePayload(payload: ContentEditorPayload): ContentEditorPayload {
  return {
    ...payload,
    mode: 'create',
    contentId: null,
    parts: Array.isArray(payload.parts)
      ? payload.parts.map(part => ({
          ...part,
          ...(Array.isArray(part.documents)
            ? { documents: part.documents.map(document => ({ ...document })) }
            : {}),
          ...(Array.isArray(part.casaPartNumbers) ? { casaPartNumbers: [...part.casaPartNumbers] } : {}),
          ...(Array.isArray(part.casaHierarchies) ? { casaHierarchies: [...part.casaHierarchies] } : {})
        }))
      : [],
    topicIds: Array.isArray(payload.topicIds) ? [...payload.topicIds] : [],
    selectedTags: Array.isArray(payload.selectedTags)
      ? payload.selectedTags.map(tag => ({ ...tag }))
      : []
  };
}

export default function ContentEditor() {
  const { t } = useTranslation();
  const languageMenuItems = useMemo(() => getExtranetEditorLanguageOptions(t), [t]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { contentID: routeContentId, routeLocale } = useParams();
  const duplicateContent = useMemo(() => {
    const state = location.state;
    if (!state || typeof state !== 'object' || !('duplicateContent' in state)) return null;
    const value = (state as { duplicateContent?: unknown }).duplicateContent;
    if (!value || typeof value !== 'object') return null;
    return value as ContentEditorPayload;
  }, [location.state]);
  const duplicateFromId = useMemo(() => {
    const state = location.state;
    if (!state || typeof state !== 'object' || !('duplicateFromId' in state)) return null;
    const value = (state as { duplicateFromId?: unknown }).duplicateFromId;
    if (value === undefined || value === null || value === '') return null;
    return value as string | number;
  }, [location.state]);
  const adminSelectedLanguage = useSelector(state => state.contents.selectedLanguage);
  const routeEditLocale = useMemo(() => {
    if (!routeContentId) return null;
    return resolveExtranetSelectableLocale(
      routeLocale || adminSelectedLanguage || resolveSelectedLanguage() || 'en'
    );
  }, [routeContentId, routeLocale, adminSelectedLanguage]);
  const defaultLang = useMemo(
    () => resolveExtranetSelectableLocale(routeEditLocale || adminSelectedLanguage || resolveSelectedLanguage() || 'en'),
    [routeEditLocale, adminSelectedLanguage]
  );
  const isEditPage = Boolean(routeContentId);
  const {
    contentId,
    locale,
    title,
    parts,
    topicIds,
    selectedTags,
    sendNotification,
    releaseDate,
    submitError,
    submitErrorDialogOpen,
    createContentPostLoading,
    createContentPostError,
    updateContentPostLoading,
    updateContentPostError,
    editorDetail,
    editorDetailLoading,
    editorDetailError
  } = useSelector(state => state.contentEditor);
  const { publishedTopics, publishedTopicsLoading, publishedTopicsError } = useSelector(state => state.topics);
  const { tagSearchResults, tagSearchLoading } = useSelector(state => state.tags);

  const [tagInputValue, setTagInputValue] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const simulateDuplicatingOverlay = useMemo(
    () => new URLSearchParams(location.search).get('simulateDuplicating') === '1',
    [location.search]
  );
  const [isDuplicatingContent, setIsDuplicatingContent] = useState(() => Boolean(duplicateFromId));
  const showDuplicatingOverlay = isDuplicatingContent || simulateDuplicatingOverlay;
  const lastHydratedDetailRef = useRef(null);
  const lastLoadedDetailRef = useRef({ contentId: null, locale: null });
  const lastFetchedTopicsLangRef = useRef(null);
  const hasInitializedLocaleRef = useRef<string | false>(false);
  const hasInitializedCreateLocaleRef = useRef(false);
  /** When set, editor language dropdown override wins over the edit URL locale. */
  const userOverrideLocaleRef = useRef(null);

  const debouncedTagSearch = useMemo(
    () =>
      debounce(query => {
        const q = typeof query === 'string' ? query.trim() : '';
        if (q) {
          dispatch(searchTags(q));
        } else {
          dispatch(clearTagSearch());
        }
      }, 300),
    [dispatch]
  );

  useEffect(() => {
    return () => debouncedTagSearch.cancel();
  }, [debouncedTagSearch]);

  useEffect(() => {
    return () => {
      dispatch(resetContentEditor());
    };
  }, [dispatch]);

  useEffect(() => {
    if (routeContentId) {
      dispatch(setMode('edit'));
      dispatch(setContentId(routeContentId));
    } else {
      dispatch(setMode('create'));
      dispatch(setContentId(null));
    }
  }, [dispatch, routeContentId]);

  useEffect(() => {
    if (routeContentId) {
      hasInitializedCreateLocaleRef.current = false;
      return;
    }
    if (duplicateContent || duplicateFromId || hasInitializedCreateLocaleRef.current) return;

    dispatch(setLocale(defaultLang));
    hasInitializedCreateLocaleRef.current = true;
  }, [dispatch, routeContentId, defaultLang, duplicateContent, duplicateFromId]);

  useEffect(() => {
    if (routeContentId || !duplicateContent) return;
    dispatch(resetContentEditor());
    dispatch(hydrateFromContentDetail(cloneHydratePayload(duplicateContent)));
  }, [dispatch, duplicateContent, routeContentId]);

  const lastDuplicateFetchRef = useRef(null);

  useEffect(() => {
    if (routeContentId || !duplicateFromId) return;
    const language = adminSelectedLanguage || defaultLang;
    const fetchKey = `${duplicateFromId}:${language}`;
    if (lastDuplicateFetchRef.current === fetchKey) return;
    lastDuplicateFetchRef.current = fetchKey;
    dispatch(fetchContentPostForEditor({ id: duplicateFromId, language }));
  }, [adminSelectedLanguage, defaultLang, dispatch, duplicateFromId, routeContentId]);

  useEffect(() => {
    if (routeContentId || !duplicateFromId || editorDetailLoading) return;
    if (!editorDetail || String(editorDetail.id) !== String(duplicateFromId)) return;
    if (lastHydratedDetailRef.current === editorDetail) return;
    const payload = toDuplicateEditorPayload(editorDetail);
    if (!payload) return;
    lastHydratedDetailRef.current = editorDetail;
    dispatch(hydrateFromContentDetail(cloneHydratePayload(payload)));
    setIsDuplicatingContent(false);
  }, [dispatch, duplicateFromId, editorDetail, editorDetailLoading, routeContentId]);

  useEffect(() => {
    if (duplicateFromId && editorDetailError) {
      setIsDuplicatingContent(false);
    }
  }, [duplicateFromId, editorDetailError]);

  // Apply edit URL locale before paint so the language field is not stuck on slice default 'en'.
  useLayoutEffect(() => {
    if (!routeContentId || !routeEditLocale) {
      hasInitializedLocaleRef.current = false;
      userOverrideLocaleRef.current = null;
      return;
    }

    const initKey = `${routeContentId}:${routeEditLocale}`;
    if (hasInitializedLocaleRef.current === initKey) return;

    userOverrideLocaleRef.current = null;
    dispatch(setLocale(routeEditLocale));
    hasInitializedLocaleRef.current = initKey;
    lastLoadedDetailRef.current = { contentId: null, locale: null };
  }, [dispatch, routeContentId, routeEditLocale]);

  useEffect(() => {
    if (!routeContentId) return;

    const lang = resolveExtranetSelectableLocale(
      userOverrideLocaleRef.current || routeEditLocale || locale || 'en'
    );

    const lastLoaded = lastLoadedDetailRef.current;
    if (lastLoaded.contentId === routeContentId && lastLoaded.locale === lang) return;

    lastLoadedDetailRef.current = { contentId: routeContentId, locale: lang };
    dispatch(fetchContentPostForEditor({ id: routeContentId, language: lang }));
  }, [routeContentId, locale, dispatch, routeEditLocale]);

  useEffect(() => {
    if (duplicateFromId) return;
    lastHydratedDetailRef.current = null;
  }, [duplicateFromId, locale, routeContentId]);

  useEffect(() => {
    if (!routeContentId) return;
    if (editorDetailLoading) return;
    if (!editorDetail || String(editorDetail.id) !== String(routeContentId)) return;
    if (lastHydratedDetailRef.current === editorDetail) return;

    // Prefer requested fetch language / edit URL over API locale metadata and slice default.
    const requestedLang = resolveExtranetSelectableLocale(
      userOverrideLocaleRef.current ||
        lastLoadedDetailRef.current?.locale ||
        routeEditLocale ||
        locale ||
        'en'
    );

    lastHydratedDetailRef.current = editorDetail;
    const payload = mapContentDetailToEditorPayload(editorDetail);
    if (payload) {
      dispatch(hydrateFromContentDetail({ ...payload, locale: requestedLang }));
    }
  }, [editorDetail, editorDetailLoading, dispatch, routeContentId, locale, routeEditLocale]);

  useEffect(() => {
    const lang = resolveExtranetSelectableLocale(
      userOverrideLocaleRef.current || routeEditLocale || defaultLang || locale || 'en'
    );
    if (lastFetchedTopicsLangRef.current === lang) return;
    lastFetchedTopicsLangRef.current = lang;
    dispatch(getPublishedTopics(lang));
  }, [dispatch, defaultLang, locale, routeEditLocale]);

  const handleLocaleChange = useCallback(
    nextLocale => {
      const resolved = resolveExtranetSelectableLocale(nextLocale || routeEditLocale || defaultLang || 'en');
      if (routeContentId) {
        if (resolved === routeEditLocale) return;
        navigate(`/extranet/content/${encodeURIComponent(String(routeContentId))}/edit/${resolved}`);
        return;
      }
      userOverrideLocaleRef.current = resolved;
      dispatch(setLocale(resolved));
    },
    [defaultLang, dispatch, navigate, routeContentId, routeEditLocale]
  );

  const hasContentDetailForRoute =
    Boolean(routeContentId) && editorDetail && String(editorDetail.id) === String(routeContentId);

  const isHydratingEditContent =
    Boolean(routeContentId) && !hasContentDetailForRoute && (editorDetailLoading || !editorDetail);

  const safeTopicTree = useMemo(() => (Array.isArray(publishedTopics) ? publishedTopics : []), [publishedTopics]);
  const topicMetaById = useMemo(() => {
    const map = new Map();
    const walk = nodes => {
      if (!Array.isArray(nodes)) return;
      nodes.forEach(node => {
        if (node?.id != null) {
          map.set(String(node.id), {
            id: node.id,
            name: typeof node.name === 'string' ? node.name : '',
            displayName:
              typeof node.displayName === 'string' && node.displayName.trim()
                ? node.displayName
                : typeof node.name === 'string'
                  ? node.name
                  : ''
          });
        }
        walk(node?.children);
      });
    };
    walk(safeTopicTree);
    return map;
  }, [safeTopicTree]);

  const tagOptions = useMemo(() => {
    const fromSearch = (tagSearchResults || []).map(t => ({
      id: t.id,
      label: t.name || t.label || String(t.id)
    }));
    const map = new Map();
    fromSearch.forEach(o => map.set(String(o.id), o));
    (selectedTags || []).forEach(o => map.set(String(o.id), o));
    return Array.from(map.values());
  }, [tagSearchResults, selectedTags]);

  const onToggleTopic = useCallback(
    topicId => {
      dispatch(toggleTopic({ topicId, topicTree: safeTopicTree }));
    },
    [dispatch, safeTopicTree]
  );

  const addTopicFromSuggestionOnly = useCallback(
    topicId => {
      dispatch(addTopicIdFromSearch({ topicId }));
    },
    [dispatch]
  );

  const onPatchPart = useCallback(
    (id, patch) => {
      dispatch(patchPart({ id, patch }));
    },
    [dispatch]
  );

  const onPartFile = useCallback(
    (part, fileOrFiles) => {
      if (part.type === 'documents') {
        const list = (Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).filter(
          f => f && typeof f.name === 'string'
        );
        if (!list.length) {
          return;
        }
        const existing = Array.isArray(part.documents) ? part.documents.map(d => ({ ...d })) : [];
        let next = [...existing];
        list.forEach(file => {
          if (next.length >= DOCUMENT_MAX_FILES) {
            return;
          }
          next.push({
            localId: uuidv4(),
            file,
            title: file.name,
            name: file.name,
            size: file.size
          });
        });
        dispatch(patchPart({ id: part.id, patch: { documents: next } }));
        return;
      }
      const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
      const prevImageUrl =
        (part.type === 'image' || part.type === 'imageText') &&
        part.image &&
        typeof part.image.url === 'string' &&
        part.image.url.startsWith('blob:')
          ? part.image.url
          : null;
      const prevVideoUrl =
        part.type === 'video' &&
        part.video &&
        typeof part.video === 'object' &&
        typeof part.video.url === 'string' &&
        part.video.url.startsWith('blob:')
          ? part.video.url
          : null;
      const toRevoke = prevImageUrl || prevVideoUrl;
      if (toRevoke) {
        try {
          URL.revokeObjectURL(toRevoke);
        } catch {
          /* ignore */
        }
      }
      if (!file) {
        if (part.type === 'image') {
          dispatch(
            patchPart({
              id: part.id,
              patch: {
                image: null,
                _file: null
              }
            })
          );
        }
        if (part.type === 'imageText') {
          dispatch(
            patchPart({
              id: part.id,
              patch: {
                image: null,
                _file: null
              }
            })
          );
        }
        if (part.type === 'video') {
          dispatch(
            patchPart({
              id: part.id,
              patch: {
                video: null,
                _file: null
              }
            })
          );
        }
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      if (part.type === 'image') {
        dispatch(
          patchPart({
            id: part.id,
            patch: {
              image: {
                ...(part.image && typeof part.image === 'object' ? part.image : {}),
                url: previewUrl,
                title: file.name,
                size: file.size,
                mimeType: file.type || '',
                alt: part.image && typeof part.image.alt === 'string' ? part.image.alt : ''
              },
              _file: file
            }
          })
        );
        return;
      }
      if (part.type === 'imageText') {
        dispatch(
          patchPart({
            id: part.id,
            patch: {
              image: {
                ...(part.image && typeof part.image === 'object' ? part.image : {}),
                url: previewUrl,
                title: file.name,
                size: file.size,
                mimeType: file.type || '',
                alt: part.image && typeof part.image.alt === 'string' ? part.image.alt : ''
              },
              _file: file
            }
          })
        );
        return;
      }
      if (part.type === 'video') {
        dispatch(
          patchPart({
            id: part.id,
            patch: {
              video: {
                ...(part.video && typeof part.video === 'object' ? part.video : {}),
                url: previewUrl,
                title: file.name,
                mimeType: file.type || ''
              },
              _file: file
            }
          })
        );
      }
    },
    [dispatch]
  );

  const handleTagInput = useCallback(
    value => {
      setTagInputValue(value);
      debouncedTagSearch(value);
    },
    [debouncedTagSearch]
  );

  const validateForm = useCallback(() => {
    const partErrors: Record<string, string> = {};
    const partFieldErrors: Record<string, { headline?: string; paragraph?: string }> = {};
    const requiredMessage = t('contentEditor.validation.partRequired');
    (Array.isArray(parts) ? parts : []).forEach(part => {
      if (!part?.id) return;
      if (part.type === 'imageText') {
        const headline = typeof part.title === 'string' && part.title.trim() ? '' : requiredMessage;
        const paragraph = typeof part.text === 'string' && part.text.trim() ? '' : requiredMessage;
        if (headline || paragraph) {
          partFieldErrors[String(part.id)] = { headline, paragraph };
        }
        return;
      }
      if (isContentEditorPartFilled(part)) return;
      partErrors[String(part.id)] =
        part.type === 'documents'
          ? t('contentEditor.validation.documentsRequired')
          : part.type === 'casaNumber'
            ? t('contentEditor.validation.casaFieldsRequired')
            : requiredMessage;
    });
    const filledParts = (Array.isArray(parts) ? parts : []).filter(isContentEditorPartFilled);

    return {
      title: !title || !title.trim() ? t('contentEditor.validation.titleRequired') : '',
      topics: !Array.isArray(topicIds) || topicIds.length === 0 ? t('contentEditor.validation.topicsRequired') : '',
      tags:
        !Array.isArray(selectedTags) || selectedTags.length === 0 ? t('contentEditor.validation.tagsRequired') : '',
      parts: filledParts.length === 0 ? t('contentEditor.validation.partsRequired') : '',
      partErrors,
      partFieldErrors
    };
  }, [title, topicIds, selectedTags, parts, t]);

  const [validationError, setValidationError] = useState('');
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const fieldErrors = useMemo(
    () =>
      showFieldErrors
        ? validateForm()
        : { title: '', topics: '', tags: '', parts: '', partErrors: {}, partFieldErrors: {} },
    [showFieldErrors, validateForm]
  );

  useEffect(() => {
    if (validationError) {
      setValidationError('');
    }
  }, [title, topicIds, selectedTags, parts]);

  const handleSavePreview = useCallback(
    async event => {
      event?.preventDefault();

      setValidationError('');
      setShowFieldErrors(true);

      const nextFieldErrors = validateForm();
      if (
        nextFieldErrors.title ||
        nextFieldErrors.topics ||
        nextFieldErrors.tags ||
        nextFieldErrors.parts ||
        Object.keys(nextFieldErrors.partErrors).length > 0 ||
        Object.keys(nextFieldErrors.partFieldErrors).length > 0
      ) {
        setValidationError(t('contentEditor.validation.mandatoryFields'));
        return;
      }
      setShowFieldErrors(false);

      try {
        // Map content parts for API structure
        const mappedContentParts = (Array.isArray(parts) ? parts : []).map((part, index) => {
          const basePart: {
            type: string;
            weight: number;
            id?: string | number;
          } = {
            type: part?.type ?? 'content',
            weight: index
          };

          // Include ID for existing parts (update mode)
          if (part?.id) {
            basePart.id = part.id;
          }

          // Add type-specific fields and preserve _file references
          switch (part?.type) {
            case 'content':
              return {
                ...basePart,
                text: typeof part.text === 'string' ? part.text : ''
              };
            case 'documents':
              return {
                ...basePart,
                documents: Array.isArray(part.documents) ? part.documents : []
              };
            case 'image':
              return {
                ...basePart,
                image: part.image,
                _file: part._file,
                _imageRemove: part._imageRemove
              };
            case 'imageText':
              return {
                ...basePart,
                title: typeof part.title === 'string' ? part.title : '',
                text: typeof part.text === 'string' ? part.text : '',
                image: part.image,
                _file: part._file,
                _imageRemove: part._imageRemove
              };
            case 'video':
              return {
                ...basePart,
                video: part.video,
                _file: part._file,
                _videoRemove: part._videoRemove
              };
            case 'salesInformationNo':
              return {
                ...basePart,
                salesInformationNo: typeof part.salesInformationNo === 'string' ? part.salesInformationNo : ''
              };
            case 'serviceInformationNo':
              return {
                ...basePart,
                siNumber: typeof part.siNumber === 'string' ? part.siNumber : ''
              };
            case 'casaNumber':
              return {
                ...basePart,
                casaLinkActive: Boolean(part.casaLinkActive),
                casaPartNumbers: Array.isArray(part.casaPartNumbers) ? [...part.casaPartNumbers] : [],
                casaHierarchies: Array.isArray(part.casaHierarchies) ? [...part.casaHierarchies] : []
              };
            default:
              return basePart;
          }
        });

        const selectedLocale = typeof locale === 'string' && locale.trim() ? locale : 'en';
        const apiLocale = resolveExtranetApiLanguageCode(selectedLocale) || selectedLocale;
        const { tagIds, newTags } = splitSelectedTags(selectedTags);

        // Build the content post payload
        const contentPost = {
          title: typeof title === 'string' ? title : '',
          locale: apiLocale,
          published: isEditPage
            ? typeof editorDetail?.published === 'boolean'
              ? editorDetail.published
              : false
            : false,
          releaseDate: releaseDate || null,
          activateNotifications: Boolean(sendNotification),
          topicIds: Array.isArray(topicIds) ? topicIds : [],
          tagIds,
          newTags,
          contentParts: mappedContentParts
        };

        let result;

        if (isEditPage) {
          // Update existing content post
          const updateId = routeContentId || contentId || editorDetail?.id;
          if (!updateId) {
            throw new Error('Content ID is required for update');
          }
          result = await dispatch(updateContentPost({ id: updateId, contentPost })).unwrap();
        } else {
          // Create new content post
          result = await dispatch(createContentPost(contentPost)).unwrap();
        }

        // On success, redirect to content detail page with locale
        const resultId = result?.id || routeContentId || contentId || editorDetail?.id;
        if (resultId) {
          navigate(`/extranet/content/${resultId}/${locale}`, { state: { fromCreateContent: !isEditPage } });
        } else {
          navigate('/extranet/content', { state: { fromCreateContent: !isEditPage } });
        }
      } catch (error) {}
    },
    [
      editorDetail?.id,
      editorDetail?.published,
      contentId,
      dispatch,
      navigate,
      isEditPage,
      locale,
      parts,
      releaseDate,
      routeContentId,
      selectedTags,
      sendNotification,
      t,
      title,
      topicIds,
      validateForm
    ]
  );

  const handleCancel = useCallback(() => {
    // navigate({ pathname: '/extranet', state: { resetExtranetTopicNav: true } });
    history.go(-1);
  }, [navigate]);

  const previewDraft = useMemo(
    () => ({ title, releaseDate, parts, selectedTags, topicIds, topicMetaById }),
    [title, releaseDate, parts, selectedTags, topicIds, topicMetaById]
  );

  return (
    <Page
      className={`content-editor-page${showDuplicatingOverlay ? ' content-editor-page--duplicating' : ''}`}
      title={
        isEditPage
          ? t('contentEditor.editPageTitle')
          : t('contentEditor.pageTitle')
      }>
      {showDuplicatingOverlay ? (
        <div className="content-editor__duplicate-overlay" role="status" aria-live="polite">
          <span className="content-editor__duplicate-spinner" aria-hidden="true" />
          <p className="content-editor__duplicate-message">{t('contentEditor.duplicating')}</p>
        </div>
      ) : null}
      <div className="content-editor-page__canvas">
      <Breadcrumb
        data={[
          { title: t('common.contentDashboard'), link: { pathname: '/administration/contents' } },
          {
            title: isEditPage
              ? t('contentEditor.editPageTitle')
              : t('contentEditor.pageTitle')
          }
        ]}
        additionalClasses="container--narrow"
      />
      <Grid className="container container--narrow">
        <div
          className={`content-editor${
            editorDetailLoading && !duplicateFromId ? ' content-editor--loading' : ''
          }`}>
        <div className="content-editor__page-header">
          <h1 className="content-editor__page-title">
            {isEditPage
              ? t('contentEditor.editPageTitle')
              : t('contentEditor.pageTitle')}
          </h1>
          {!isHydratingEditContent && (
            <Button
              type="button"
              secondary
              label={t('contentEditor.preview')}
              additionalClassName="content-editor__preview-button"
              onClick={() => setPreviewOpen(true)}
            />
          )}
        </div>

        {isHydratingEditContent ? (
          <div className="content-editor__loading-state">{t('common.loading')}</div>
        ) : (
          <>
            <ContentEditorBlocks
              languageOptions={languageMenuItems}
              locale={locale}
              defaultLocale={defaultLang}
              onLocaleChange={handleLocaleChange}
              title={title}
              titleError={fieldErrors.title}
              partErrors={fieldErrors.partErrors}
              partFieldErrors={fieldErrors.partFieldErrors}
              onTitleChange={v => dispatch(setTitle(v))}
              parts={parts}
              onAddPart={type => dispatch(addPart({ type }))}
              onPatchPart={onPatchPart}
              onRemovePart={id => dispatch(removePart({ id }))}
              onMoveUp={index => dispatch(movePartUp({ index }))}
              onMoveDown={index => dispatch(movePartDown({ index }))}
              onReorderPart={(fromId, toId) => dispatch(movePartToIndex({ fromId, toId }))}
              onPartFile={onPartFile}
            />

            <ContentEditorSettings
              topicTree={safeTopicTree}
              topicIds={topicIds}
              topicTreeLoading={publishedTopicsLoading}
              topicTreeError={publishedTopicsError}
              topicsError={fieldErrors.topics}
              tagsError={fieldErrors.tags}
              onToggleTopic={onToggleTopic}
              onAddTopicFromSearch={addTopicFromSuggestionOnly}
              tagInputValue={tagInputValue}
              onTagInputChange={handleTagInput}
              tagOptions={tagOptions}
              tagLoading={tagSearchLoading}
              selectedTags={selectedTags}
              onSelectedTagsChange={tags => dispatch(setSelectedTags(tags))}
              sendNotification={sendNotification}
              onSendNotificationChange={v => dispatch(setSendNotification(v))}
              releaseDate={releaseDate}
              onReleaseDateChange={v => dispatch(setReleaseDate(v))}
            />

            <div className="content-editor__footer">
              {validationError && <div className="content-editor__validation-error">{validationError}</div>}
              <Button
                type="button"
                label={
                  createContentPostLoading || updateContentPostLoading
                    ? t('contentEditor.saving')
                    : isEditPage
                      ? t('contentEditor.saveAndPreview')
                      : t('contentEditor.create')
                }
                onClick={handleSavePreview}
                disabled={createContentPostLoading || updateContentPostLoading}
              />
              <Button
                type="button"
                secondary
                label={t('common.button.cancel')}
                onClick={handleCancel}
                disabled={createContentPostLoading || updateContentPostLoading}
              />
            </div>
          </>
        )}
        </div>
      </Grid>
      </div>

      <ContentEditorPreview open={previewOpen} onClose={() => setPreviewOpen(false)} draft={previewDraft} />

      <ContentEditorErrorDialog
        open={
          submitErrorDialogOpen ||
          Boolean(createContentPostError) ||
          Boolean(updateContentPostError) ||
          Boolean(duplicateFromId && editorDetailError)
        }
        message={String(
          submitError ||
            createContentPostError ||
            updateContentPostError ||
            editorDetailError ||
            t('administrationPage.extranetContents.duplicateError')
        )}
        onClose={() => {
          const shouldReturnToContents = Boolean(duplicateFromId && editorDetailError);
          dispatch(closeSubmitErrorDialog());
          dispatch(clearCreateContentPostError());
          dispatch(clearUpdateContentPostError());
          dispatch(clearEditorDetailError());
          if (shouldReturnToContents) {
            navigate('/administration/contents');
          }
        }}
      />
    </Page>
  );
}
